import React, { createContext, useCallback, useContext, useEffect, useId, useState } from 'react';
import styled from 'styled-components';
import glassTheme from '../../styles/glassTheme';

type RegisterNotes = (id: string, notes: string[]) => () => void;
const NotesContext = createContext<RegisterNotes | null>(null);
const Footer = styled.footer`
  ${glassTheme.glass.light}
  margin-top: ${glassTheme.spacing.xl}; padding: ${glassTheme.spacing.md};
  summary { cursor: pointer; font-weight: 600; min-height: 44px; padding: ${glassTheme.spacing.sm} 0; }
  p, li { color: ${glassTheme.colors.text.secondary}; line-height: 1.7; }
  li + li { margin-top: ${glassTheme.spacing.sm}; }
`;

/** Independently loaded panels register only their current notes, never student data. */
export function useRecordNotes(notes: string[]) {
  const register = useContext(NotesContext);
  const id = useId();
  const key = JSON.stringify(Array.from(new Set(notes)).sort());
  useEffect(() => {
    const current: string[] = JSON.parse(key);
    if (register && current.length) return register(id, current);
  }, [register, id, key]);
}

export function RecordNotesLayout({children}: {children: React.ReactNode}) {
  const [entries, setEntries] = useState<Record<string, string[]>>({});
  const register = useCallback<RegisterNotes>((id, notes) => {
    setEntries(previous => ({...previous, [id]: notes}));
    return () => setEntries(previous => {
      const next = {...previous};
      delete next[id];
      return next;
    });
  }, []);
  const notes = Array.from(new Set(Object.values(entries).flat())).sort();
  return <NotesContext.Provider value={register}>
    {children}
    <Footer aria-label="记录说明">
      <details>
        <summary>记录说明</summary>
        <h3>指标口径</h3>
        <ul>
          <li>“0”表示已记录的零值；“暂无数据”或“待核实”不等于没有学习。“部分记录”表示数据不完整或来源存在限制，覆盖人数不等于参与人数。</li>
          <li>完成进度按当前课程知识元统计，同一知识元多处引用时课程总数只计一次；空章节不计入完成章节总数。完成课程指全部知识元均有完成记录。</li>
          <li>课程和知识元汇总包含全部在籍学生，不受姓名搜索或分页影响；标注“本页”的图表仅比较当前页。条形空白不代表未完成或未参与。</li>
          <li>练习次数仅统计已保留的尝试记录，可能包含重复提交，不代表完整提交次数。</li>
          <li>新版前台用时仅计内容就绪、页面可见且位于前台的时间，以秒显示；不代表认知投入、视频播放或外部窗口学习。单题用时未知，不按活动时长分摊。</li>
          <li>跨标签页／设备的重叠可能未核实，异常关闭可能丢失尾段。历史时长原值的单位和范围未核实，不与新版用时相加；独立知识元和工作区用时不计入课程。</li>
          <li>自我解释仅展示本课程活动中保留的记录，独立知识元解释不向教师开放；有版本差异时展示最新保留版本。时间戳按当前设备时区显示。</li>
        </ul>
        {notes.length > 0 && <><h3>本次数据说明</h3><ul>{notes.map(note => <li key={note}>{note}</li>)}</ul></>}
      </details>
    </Footer>
  </NotesContext.Provider>;
}
